import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Building2, Users, Activity, Shield, AlertTriangle, TrendingUp, Database, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import platformApi from '@/lib/platformApi';

export default function PlatformLogin({ onLogin }) {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await platformApi.post('/platform/auth/login', { email, password });
      const { access_token, user } = response.data;
      
      if (user.role !== 'PLATFORM_OWNER') {
        toast.error('Access denied. Platform owner privileges required.');
        return;
      }
      
      localStorage.setItem('platform_token', access_token);
      localStorage.setItem('platform_user', JSON.stringify(user));
      onLogin(user, access_token);
      toast.success('Welcome to Platform Admin');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl mb-4">
            <Shield className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white">Platform Admin</h1>
          <p className="text-slate-400 mt-2">Zesta Healthcare Management Console</p>
        </div>

        <Card className="bg-white border-slate-200 shadow-lg">
          <CardHeader>
            <CardTitle className="text-slate-900 text-center">Super Admin Login</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  placeholder="platform@zestahealthcare.com"
                  required
                  data-testid="platform-email-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  placeholder="••••••••"
                  required
                  data-testid="platform-password-input"
                />
              </div>
              <div className="text-right mb-2">
                <a 
                  href="/reset-password?type=platform" 
                  className="text-sm text-emerald-400 hover:text-emerald-300"
                >
                  Forgot Password?
                </a>
              </div>
              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white py-2.5"
                disabled={loading}
                data-testid="platform-login-btn"
              >
                {loading ? 'Signing in...' : 'Sign in to Platform Admin'}
              </Button>
            </form>

            <div className="mt-6 pt-6 border-t border-slate-700">
              <p className="text-xs text-slate-500 text-center">
                This portal is restricted to platform administrators only.
                <br />
                Hospital staff should use the{' '}
                <button 
                  onClick={() => navigate('/login')}
                  className="text-emerald-400 hover:text-emerald-300"
                >
                  standard login
                </button>
              </p>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-slate-500 text-xs mt-6">
          Zesta Healthcare Platform © 2026
        </p>
      </div>
    </div>
  );
}
